import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SystemSettings {
  // 站点级对外信息（名称 / 联系方式 / 备案号）不在这里存：一律读后台 system_info，
  // 每用户本地偏好只保留下面这些纯展示与默认值项。
  defaultPageSize: number;
  currency: string;
  dateFormat: string;
  timeFormat: string;
  language: string;
  timezone: string;
  notificationEnabled: boolean;
  auditLogEnabled: boolean;
  defaultNoteTemplate?: string;
  notificationPrefs?: Record<string, boolean>;
}

interface SettingState {
  settings: SystemSettings;
  loading: boolean;
  error: string | null;

  fetchSettings: () => Promise<void>;
  updateSettings: (updates: Partial<SystemSettings>) => Promise<{ error: Error | null }>;
  resetSettings: () => Promise<{ error: Error | null }>;
  resetStore: () => void;
}

const DEFAULT_SETTINGS: SystemSettings = {
  defaultPageSize: 10,
  currency: 'CNY',
  dateFormat: 'YYYY-MM-DD',
  timeFormat: 'HH:mm:ss',
  language: 'zh-CN',
  timezone: 'Asia/Shanghai',
  notificationEnabled: true,
  auditLogEnabled: true,
  defaultNoteTemplate: '',
  notificationPrefs: {},
};

/**
 * 用户偏好设置。
 *
 * 说明：语言/时区/日期格式/通知开关等均为「每用户本地偏好」，不涉及服务端数据，
 * 通过 zustand persist 落地 localStorage（此前调用 GET/PUT /settings 的服务端端点
 * 并不存在，保存一直静默失败，已改为真实的本地持久化）。
 * 站点级配置（站点名称/SMTP/短信等）由 admin 后台的配置中心统一管理。
 */
export const useSettingStore = create<SettingState>()(
  persist(
    (set) => ({
      settings: { ...DEFAULT_SETTINGS },
      loading: false,
      error: null,

      fetchSettings: async () => {
        // 本地偏好已由 persist 在启动时恢复，无需异步加载
      },

      updateSettings: async (updates: Partial<SystemSettings>) => {
        set((s) => ({ settings: { ...s.settings, ...updates }, error: null }));
        return { error: null };
      },

      resetSettings: async () => {
        set({ settings: { ...DEFAULT_SETTINGS }, error: null });
        return { error: null };
      },

      resetStore: () => {
        set({ settings: { ...DEFAULT_SETTINGS }, loading: false, error: null });
      },
    }),
    {
      name: 'setting-storage',
      partialize: (state) => ({ settings: state.settings }),
    }
  )
);
